import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { authService } from '../ory/service';

/**
 * bfcache（Back-Forward Cache）対策フック
 *
 * ブラウザバックでページがキャッシュから復元された場合、
 * whoami APIを呼び出してセッション状態を再チェックし、
 * 認証状態に応じて適切なリダイレクトを行います。
 *
 * 認証ロジック:
 * - 401（未認証）→ サインインページに遷移
 * - 200でmfa.enabled=false → そのままアクセス可能
 * - 200でmfa.enabled=trueかつaal1 → 追加認証ページ（サインインページ）に遷移
 * - 200でmfa.enabled=trueかつaal2 → そのままアクセス可能
 *
 * @example
 * ```tsx
 * function ProtectedLayout() {
 *   useBfcacheSessionCheck();
 *   return <Outlet />;
 * }
 * ```
 */
export function useBfcacheSessionCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckingRef = useRef(false);
  useEffect(() => {
    const handlePageShow = async (event: PageTransitionEvent) => {
      // bfcacheから復元された場合のみチェック
      if (!event.persisted) return;

      // /auth配下の場合はチェックをスキップ
      if (location.pathname.startsWith('/auth')) return;

      // 重複チェックを防止
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const status = await authService.getAuthenticationStatus();
        if (status === 'unauthenticated' || status === 'requires_mfa') {
          navigate({
            to: '/auth/sign-in',
            search: {
              redirect: location.pathname
            },
            replace: true
          });
        }
      } catch (error) {
        console.error('Failed to check session on pageshow:', error);
        // エラーの場合も安全のためリダイレクト
        navigate({
          to: '/auth/sign-in',
          search: {
            redirect: location.pathname
          },
          replace: true
        });
      } finally {
        isCheckingRef.current = false;
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [navigate, location.pathname]);
}